import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@clerk/tanstack-react-start'
import { Alert, Button, Container, Grid, Group, Progress, Stack, Text } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { IconAdjustments, IconAlertTriangle, IconSparkles } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { REFINEMENT_RAIL_WIDTH } from '../constants/refinement-layout'
import {
  aggregateToolsAndMaterialsFromTemplate,
  buildAnswerChipLabels,
  buildCustomizationSummary,
  calculateReadingTime,
} from '../utils'
import {
  getDiyAnswersKey,
  getDiyPersonalizeDismissedKey,
  readDiyRefinementAnswersFromLocalStorage,
} from '../utils/living-guide-storage'
import { useContentTier } from '../context/ContentTierContext'
import { useUrlPresets } from '../hooks/useUrlPresets'
import { usePersonalizedPlan } from '../hooks/usePersonalizedPlan'
import { useAnonymousRefinementUsage } from '../hooks/useAnonymousRefinementUsage'
import { useLivingGuideClaim } from '../hooks/useLivingGuideClaim'
import { useGuideProgress } from '../hooks/useGuideProgress'
import { DiyAnimationBudgetProvider } from './DiyAnimationBudgetContext'
import { ProjectGuideHeader } from './ProjectGuideHeader'
import { ProjectGuideOverview } from './ProjectGuideOverview'
import { ProjectGuideSteps } from './ProjectGuideSteps'
import { ProjectGuideConversionCTA } from './ProjectGuideConversionCTA'
import { ProjectGuideFAQ } from './ProjectGuideFAQ'
import { RefinePlanSheet } from './RefinePlanSheet'
import { ProjectGuideStructuredData } from './ProjectGuideStructuredData'
import { ProjectGuideMeta } from './ProjectGuideMeta'
import { ProjectGuideTOC } from './ProjectGuideTOC'
import { RelatedProjects } from './RelatedProjects'
import type {AnswerDelta} from '../utils';
import type { AnchorFABAction } from '@/components/AnchorFAB'
import type { CustomizationSummary, ProjectTemplate } from '../types'
import { useFAB } from '@/components/AnchorFAB'
import { usePostHog } from '@/lib/posthog'
import {
  trackAiChatOpened,
  trackConversionCompleted,
  trackPlanCustomizationCompleted,
  trackPlanCustomizationStarted,
  trackSaveCtaClicked,
  trackTocNavigation,
} from '@/features/diy/utils/analytics'

const SAFETY_DISCLAIMERS: Record<string, string> = {
  electrical:
    'Electrical work often requires a permit and inspection in your jurisdiction. Wiring errors can cause fires or electrocution. Stop and consult a licensed electrician if you are unsure at any step.',
  roofing:
    'Roofing work involves serious fall hazards. Use proper fall protection and consider hiring a licensed contractor for steep or high roofs.',
  hvac:
    'HVAC work involving gas lines or refrigerants must be performed by a licensed technician. Improper connections can cause gas leaks, fires, or carbon monoxide exposure.',
}

interface RelatedProjectData {
  _id: string
  slug: string
  title: string
  description: string
  category: string
  difficulty: string
  estimatedHours?: number
  estimatedCostMin?: number
  estimatedCostMax?: number
  heroImageUrl?: string
  toolOverlapCount: number
  additionalToolsNeeded: number
}

interface ProjectGuidePageProps {
  template: ProjectTemplate
  relatedProjects?: Array<RelatedProjectData>
  deltas?: Array<AnswerDelta>
}

export function ProjectGuidePage({ template, relatedProjects, deltas = [] }: ProjectGuidePageProps) {
  const navigate = useNavigate()
  const { isSignedIn } = useAuth()
  const posthog = usePostHog()
  const { setActions, setBadge } = useFAB()
  const { tier, isLoading: tierLoading } = useContentTier()
  const [refineOpened, { open: openRefineBase, close: closeRefine }] = useDisclosure(false)
  const [refineDefaultTab, setRefineDefaultTab] = useState<'guided' | 'ai'>('guided')

  const openRefine = useCallback(
    (sub: 'guided' | 'ai' = 'guided') => {
      setRefineDefaultTab(sub)
      openRefineBase()
    },
    [openRefineBase]
  )

  // Track if we've hydrated (client-only state is ready)
  const [isHydrated, setIsHydrated] = useState(false)

  // Personalize prompt dismissal — false until hydration reads localStorage
  const [personalizePromptDismissed, setPersonalizePromptDismissed] = useState(true)

  // Answers state - always initialize empty for SSR consistency
  const [answers, setAnswers] = useState<Record<string, string>>({})

  // After hydration, load answers and dismissal flag from localStorage
  useEffect(() => {
    setIsHydrated(true)
    const loaded = readDiyRefinementAnswersFromLocalStorage(template.slug)
    if (Object.keys(loaded).length > 0) {
      setAnswers(loaded)
    }
    const dismissed = localStorage.getItem(getDiyPersonalizeDismissedKey(template.slug))
    setPersonalizePromptDismissed(dismissed === 'true')
  }, [template.slug])

  const planCustomizationStartedRef = useRef(false)

  useEffect(() => {
    planCustomizationStartedRef.current = false
  }, [template.slug])

  // Persist answers to storage when they change (skip initial empty state)
  useEffect(() => {
    if (isHydrated && Object.keys(answers).length > 0) {
      localStorage.setItem(getDiyAnswersKey(template.slug), JSON.stringify(answers))
    }
  }, [answers, template.slug, isHydrated])

  const guideProgress = useGuideProgress(template.slug)

  const { markSignupFromGuide, alternateClaimModal, savePlanFromGuide } = useLivingGuideClaim({
    templateId: template._id,
    templateSlug: template.slug,
    answers,
    isHydrated,
    setAnswers,
    onClaimSuccess: (payload) => {
      trackConversionCompleted(posthog, payload)
    },
  })

  const {
    refinementCount: qaRefinementCount,
    isAnonLimitActive,
    handleAnswerChange,
    atRefinementLimit,
  } = useAnonymousRefinementUsage({
    template,
    templateSlug: template.slug,
    deltas,
    isHydrated,
    setAnswers,
  })

  const handleAnswerChangeTracked = useCallback(
    (key: string, value: string) => {
      const prevEmpty = (answers[key] ?? '').trim() === ''
      const valueNonEmpty = value.trim() !== ''
      handleAnswerChange(key, value)
      if (prevEmpty && valueNonEmpty && !planCustomizationStartedRef.current) {
        planCustomizationStartedRef.current = true
        trackPlanCustomizationStarted(posthog, {
          projectSlug: template.slug,
          questionKey: key,
          source: 'customize_sheet',
        })
      }
    },
    [answers, handleAnswerChange, posthog, template.slug]
  )

  // Bulk update answers (used by URL presets)
  const handleApplyAnswers = useCallback((newAnswers: Record<string, string>) => {
    setAnswers(newAnswers)
  }, [])
  
  // Apply URL presets on initial load
  useUrlPresets({
    urlPresets: template.urlPresets,
    templateSlug: template.slug,
    currentAnswers: answers,
    onApplyAnswers: handleApplyAnswers,
  })
  
  // Apply deltas and track changes
  const {
    personalizedSteps,
    estimatedHours,
    estimatedCostMin,
    estimatedCostMax,
    modifiedStepSlugs,
    isPersonalized,
  } = usePersonalizedPlan({
    template,
    answers,
    deltas,
    enabled: isHydrated,
  })

  // Check if customized - only after hydration to prevent mismatch
  // URL presets that only adjust SEO (no answerMapping) must not set this — answers stay
  // empty and the header CTA must match FAB state (see Story 2.2 code review).
  const isCustomized =
    isHydrated && (Object.keys(answers).length > 0 || isPersonalized)

  // Aggregate template-level + step-level tools and materials
  const { tools, materials } = useMemo(
    () =>
      aggregateToolsAndMaterialsFromTemplate(
        { ...template, steps: personalizedSteps },
        personalizedSteps,
      ),
    [template, personalizedSteps],
  )

  // Build customization summary (key answers) — fallback when personalized without chip labels
  const customizationSummary: CustomizationSummary | undefined = useMemo(() => {
    if (!isCustomized) return undefined
    return buildCustomizationSummary(template, answers, deltas)
  }, [answers, deltas, isCustomized, template])

  const answerChipLabels = useMemo(
    () => (isCustomized ? buildAnswerChipLabels(template, answers) : []),
    [answers, isCustomized, template]
  )

  const estimateComparison = useMemo(() => {
    if (!isHydrated) return undefined
    const baseH = template.estimatedHours ?? 0
    const baseMin = template.estimatedCostMin ?? 0
    const baseMax = template.estimatedCostMax ?? 0
    const hoursDiffer = estimatedHours !== baseH
    const costDiffer =
      estimatedCostMin !== baseMin || estimatedCostMax !== baseMax
    if (!hoursDiffer && !costDiffer) return undefined
    return {
      baseHours: baseH,
      baseCostMin: template.estimatedCostMin,
      baseCostMax: template.estimatedCostMax,
    }
  }, [
    estimatedCostMax,
    estimatedCostMin,
    estimatedHours,
    isHydrated,
    template.estimatedCostMax,
    template.estimatedCostMin,
    template.estimatedHours,
  ])

  const handleDismissPersonalizePrompt = useCallback(() => {
    setPersonalizePromptDismissed(true)
    localStorage.setItem(getDiyPersonalizeDismissedKey(template.slug), 'true')
  }, [template.slug])

  // Anonymous: sign-up intent + redirect. Signed-in: upsert guide answers (same path as claim modal).
  const handleSave = useCallback(
    async (ctaSource: 'toc' | 'fab' | 'inline' = 'inline') => {
      trackSaveCtaClicked(posthog, {
        projectSlug: template.slug,
        isCustomized,
        source: ctaSource,
      })
      if (!isSignedIn) {
        markSignupFromGuide()
        navigate({
          to: '/sign-up',
          search: {
            redirect_url: `/diy/${template.slug}`,
          },
        })
        return
      }

      const result = await savePlanFromGuide()
    switch (result) {
      case 'profile-not-ready':
        notifications.show({
          title: 'Almost there',
          message: 'Your account is still loading. Try again in a moment.',
          color: 'blue',
        })
        break
      case 'no-answers':
        notifications.show({
          title: 'Nothing to save yet',
          message: 'Use Refine plan to answer a question first.',
          color: 'blue',
        })
        break
      case 'already-saved':
        notifications.show({
          title: 'Already saved',
          message: 'This plan is in your account. Opening your dashboard.',
          color: 'green',
        })
        navigate({ to: '/dashboard' })
        break
      case 'upsert-finished':
        // Success / error toasts from useLivingGuideClaim.runClaim
        break
    }
  }, [
    isCustomized,
    isSignedIn,
    markSignupFromGuide,
    navigate,
    posthog,
    savePlanFromGuide,
    template.slug,
  ])

  const handleSignInFromGuide = useCallback(() => {
    markSignupFromGuide()
    navigate({
      to: '/sign-in',
      search: {
        redirect_url: `/diy/${template.slug}`,
      },
    })
  }, [markSignupFromGuide, navigate, template.slug])

  // Handle share action
  const handleShare = useCallback(async () => {
    const url = window.location.href
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: template.title, url })
      } else {
        await navigator.clipboard.writeText(url)
        notifications.show({
          title: 'Link copied',
          message: 'Project link copied to clipboard',
          color: 'green',
        })
      }
    } catch (e: unknown) {
      const name = e instanceof Error ? e.name : ''
      if (name === 'AbortError') return
      console.error('[ProjectGuidePage] share failed', e)
      notifications.show({
        title: 'Could not share',
        message: 'Copy the address from your browser bar or try again.',
        color: 'red',
      })
    }
  }, [template.title])

  // Single FAB action → opens unified refine surface (Story 2.13); share/save live in the header
  useEffect(() => {
    const actions: Array<AnchorFABAction> = [
      {
        id: 'refine',
        icon: IconAdjustments,
        label: 'Refine plan',
        badge: !isCustomized,
        onPress: () => openRefine('guided'),
      },
    ]

    setActions(actions)
    setBadge(!isCustomized)

    return () => {
      setActions([])
      setBadge(false)
    }
  }, [isCustomized, openRefine, setActions, setBadge])

  const readingTime = useMemo(() => calculateReadingTime(template), [template])

  const answersFingerprint = useMemo(
    () =>
      Object.keys(answers)
        .sort()
        .map((k) => `${k}:${answers[k]}`)
        .join('|'),
    [answers]
  )
  
  // Use initialValue: false and getInitialValueInEffect: false
  // This ensures SSR and first client render both use false, preventing hydration mismatch
  // The actual media query value is applied after hydration
  const isDesktop = useMediaQuery('(min-width: 1024px)', false, {
    getInitialValueInEffect: false,
  })

  const showRefinementRail = Boolean(isDesktop) && refineOpened

  const showQaRefinementUsage =
    isHydrated && !tierLoading && isAnonLimitActive

  const hasRefinementQuestions = (template.refinementQuestions?.length ?? 0) > 0
  const showSignedInQaUsage =
    isHydrated &&
    !tierLoading &&
    (tier === 'free' || tier === 'premium') &&
    hasRefinementQuestions

  const suppressAnonRefinementInHeader = atRefinementLimit && refineOpened
  const showAnonCappedInHeader =
    showQaRefinementUsage && !suppressAnonRefinementInHeader

  const mainContent = (
    <DiyAnimationBudgetProvider maxConcurrent={3}>
    <Stack gap="lg">
      {/* Progress bar — only shown for signed-in users with a claimed project */}
      {guideProgress.projectId && (
        <Stack gap={4}>
          <Group justify="space-between">
            <Text size="sm" fw={500}>Your progress</Text>
            <Text size="sm" c="dimmed">
              {guideProgress.completedSteps} / {guideProgress.totalSteps} steps
            </Text>
          </Group>
          <Progress
            value={guideProgress.progressPercent}
            size="sm"
            radius="xl"
            color={guideProgress.progressPercent === 100 ? 'green' : 'primary'}
          />
        </Stack>
      )}

      {/* Article metadata (reading time, breadcrumbs) */}
      <ProjectGuideMeta
        category={template.category}
        readingTimeMinutes={readingTime}
        title={template.title}
      />

      {/* Safety disclaimer for high-risk categories */}
      {SAFETY_DISCLAIMERS[template.category] && (
        <Alert
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
          title="Safety notice"
        >
          {SAFETY_DISCLAIMERS[template.category]}
        </Alert>
      )}

      {/* Table of Contents (inline on mobile only) */}
      {!isDesktop && (
        <ProjectGuideTOC
          steps={personalizedSteps}
          sections={template.sections}
          isCustomized={isCustomized}
          onSave={() => {
            void handleSave('toc')
          }}
          onTocNavigate={(sectionId) => {
            trackTocNavigation(posthog, {
              projectSlug: template.slug,
              targetSection: sectionId,
            })
          }}
        />
      )}

      {/* Header with stats */}
      <ProjectGuideHeader
        title={template.title}
        difficulty={template.difficulty}
        estimatedHours={estimatedHours}
        estimatedCostMin={estimatedCostMin}
        estimatedCostMax={estimatedCostMax}
        tools={tools}
        materials={materials}
        isCustomized={isCustomized}
        customizationSummary={customizationSummary}
        answerChipLabels={answerChipLabels}
        onEditCustomization={() => openRefine('guided')}
        onShareProject={handleShare}
        onSaveMyPlan={() => {
          void handleSave('inline')
        }}
        answersFingerprint={answersFingerprint}
        anonymousRefinement={
          showSignedInQaUsage
            ? {
                show: true,
                count: qaRefinementCount,
                display: 'total',
                accountMode: 'signedIn',
              }
            : showAnonCappedInHeader
            ? {
                show: true,
                count: qaRefinementCount,
                display: 'capped',
                accountMode: 'anon',
                onSaveProgress: () => {
                  void handleSave('fab')
                },
                onSignUp: () => {
                  void handleSave('fab')
                },
                onSignIn: handleSignInFromGuide,
              }
            : undefined
        }
        estimateComparison={estimateComparison}
      />

      {/* Overview / Blog Content */}
      <ProjectGuideOverview
        content={template.overviewContent ?? template.description}
        heroImageUrl={template.heroImageUrl}
        title={template.title}
      />

      {/* Personalize prompt — authenticated users with no answers yet */}
      {isSignedIn && isHydrated && !isCustomized && !personalizePromptDismissed && (template.refinementQuestions?.length ?? 0) > 0 && (
        <Alert
          color="primary"
          variant="light"
          icon={<IconSparkles size={16} />}
          closeButtonLabel="Dismiss"
          withCloseButton
          onClose={handleDismissPersonalizePrompt}
        >
          <Group justify="space-between" wrap="nowrap" gap="md">
            <Stack gap={2}>
              <Text size="sm" fw={600}>Make this guide yours</Text>
              <Text size="sm" c="dimmed">
                Answer a few quick questions to tailor the steps, tools, and costs to your situation.
              </Text>
            </Stack>
            <Button size="xs" variant="filled" onClick={() => openRefine('guided')} style={{ flexShrink: 0 }}>
              Personalize
            </Button>
          </Group>
        </Alert>
      )}

      {/* Step-by-Step Plan */}
      <ProjectGuideSteps
        steps={personalizedSteps}
        sections={template.sections}
        templateSlug={template.slug}
        projectSlug={template.slug}
        modifiedStepSlugs={modifiedStepSlugs}
        defaultExpandedStep={0}
        isStepCompleted={guideProgress.projectId ? guideProgress.isStepCompleted : undefined}
        onStepComplete={guideProgress.projectId ? guideProgress.handleStepComplete : undefined}
        isStepUpdating={guideProgress.projectId ? guideProgress.isStepUpdating : undefined}
      />

      {/* Conversion CTA - only show for anon users */}
      {tier === 'anon' && (
        <ProjectGuideConversionCTA
          slug={template.slug}
          isCustomized={isCustomized}
          onBeforeAuthNavigation={markSignupFromGuide}
        />
      )}

      {/* FAQ */}
      {template.faqItems && template.faqItems.length > 0 && (
        <ProjectGuideFAQ items={template.faqItems} projectSlug={template.slug} />
      )}

      {/* Related Projects (after FAQ per spec) */}
      {relatedProjects && relatedProjects.length > 0 && (
        <RelatedProjects projects={relatedProjects} sourceProjectSlug={template.slug} />
      )}
    </Stack>
    </DiyAnimationBudgetProvider>
  )

  return (
    <>
      {/* SEO: Structured data for Google rich results */}
      <ProjectGuideStructuredData template={template} />

      <Container
        size={isDesktop ? 'lg' : 'sm'}
        py="lg"
        px={{ base: 'xs', sm: 'md' }}
        style={
          showRefinementRail
            ? { paddingRight: REFINEMENT_RAIL_WIDTH }
            : undefined
        }
      >
        {isDesktop ? (
          <Grid gutter="xl">
            {/* Sticky TOC sidebar */}
            <Grid.Col span={3}>
              <ProjectGuideTOC
                steps={personalizedSteps}
                sections={template.sections}
                isCustomized={isCustomized}
                onSave={() => {
                  void handleSave('toc')
                }}
                onTocNavigate={(sectionId) => {
                  trackTocNavigation(posthog, {
                    projectSlug: template.slug,
                    targetSection: sectionId,
                  })
                }}
              />
            </Grid.Col>
            {/* Main content */}
            <Grid.Col span={9}>{mainContent}</Grid.Col>
          </Grid>
        ) : (
          mainContent
        )}
      </Container>

      <RefinePlanSheet
        opened={refineOpened}
        onClose={closeRefine}
        layout={isDesktop ? 'rail' : 'drawer'}
        defaultSubTab={refineDefaultTab}
        hasGuidedQuestions={hasRefinementQuestions}
        questions={template.refinementQuestions ?? []}
        answers={answers}
        onAnswerChange={handleAnswerChangeTracked}
        anonymousRefinement={
          isHydrated && isAnonLimitActive
            ? {
                count: qaRefinementCount,
                showBar: showQaRefinementUsage,
                atLimit: atRefinementLimit,
                templateSlug: template.slug,
                display: 'capped',
                accountMode: 'anon',
                onBeforeAuthNavigation: markSignupFromGuide,
                onSaveProgress: () => {
                  void handleSave('fab')
                },
                onSignUp: () => {
                  void handleSave('fab')
                },
                onSignIn: handleSignInFromGuide,
              }
            : isHydrated && showSignedInQaUsage
            ? {
                count: qaRefinementCount,
                showBar: true,
                atLimit: false,
                templateSlug: template.slug,
                display: 'total',
                accountMode: 'signedIn',
              }
            : undefined
        }
        templateTitle={template.title}
        templateSlug={template.slug}
        onAiSubmit={() => {
          notifications.show({
            title: 'AI refinement coming soon',
            message: 'Use Quick questions to personalize your plan for now.',
            color: 'blue',
            autoClose: 6000,
          })
        }}
        onGuidedQuestionnaireCompleted={(payload) => {
          trackPlanCustomizationCompleted(posthog, {
            projectSlug: template.slug,
            ...payload,
          })
        }}
        onAiSurfaceShown={() => {
          trackAiChatOpened(posthog, { projectSlug: template.slug })
        }}
      />

      {alternateClaimModal}
    </>
  )
}
